import { useEffect, useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, fmtDate, inr } from "@/lib/api";
import {
  useApi, post, put, Panel, PageHead, Stat, DataTable, StatusBadge, Tabs,
} from "@/crm/ui";
import { Download, Printer, Copy, QrCode, Plus, AlertTriangle } from "lucide-react";

const OPEN = new Set(["Branch Assigned", "With Telecaller", "With Sales"]);
const emptyForm = {
  mobile: "", first_name: "", middle_name: "", last_name: "", product_type: "",
  loan_amount: "", alt_phone: "", dob: "", email: "", pan: "", employment_type: "",
  lead_source: "", city: "", pincode: "", assign_to: "", comment: "",
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="text-xs">
      {label}{required && <span className="text-red-500"> *</span>}
    </Label>
    {children}
  </div>
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">
    <option value="">Select…</option>
    {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
  </select>
);

export default function Enquiries() {
  const { user } = useAuth();
  const isManager = ["admin", "manager", "operations"].includes(user?.role);
  const [tab, setTab] = useState("Enquiries");
  const [status, setStatus] = useState("all");
  const meta = useApi("/crm/enquiries/meta");
  const list = useApi(`/crm/enquiries${status !== "all" ? `?status=${status}` : ""}`, [status]);
  const rows = list.data || [];
  const m = meta.data || {};
  const options = m.options || {};
  const assignable = useMemo(
    () => [...(m.telecallers || []), ...(m.sales || [])].map((u) => ({
      value: u.email, label: `${u.name} · ${u.role}`,
    })), [meta.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const publicUrl = `${window.location.origin}/enquiry/new`;
  const qrRef = useRef(null);

  const submitCreate = async () => {
    if (!form.mobile.trim()) return toast.error("Mobile is required");
    if (!Number(form.loan_amount)) return toast.error("Required Loan Amount is required");
    const r = await post("/crm/enquiries", form, "Enquiry created");
    if (r) { setCreateOpen(false); setForm(emptyForm); list.reload(); }
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "GrowRupee_Enquiry_QR.png";
    a.click();
  };
  const printQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const w = window.open("", "_blank");
    w.document.write(
      `<html><head><title>Enquiry QR</title></head><body style="text-align:center;font-family:sans-serif;padding:40px">
       <h2>Scan to submit your loan enquiry</h2>
       <img src="${canvas.toDataURL("image/png")}" style="width:320px;height:320px"/>
       <p>${publicUrl}</p></body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const stats = {
    total: rows.length,
    open: rows.filter((r) => OPEN.has(r.status)).length,
    converted: rows.filter((r) => r.status === "Converted").length,
    closed: rows.filter((r) => r.status === "Not Interested").length,
  };
  const tabs = ["Enquiries", "QR Code", ...(isManager ? ["Branches", "Options"] : [])];

  return (
    <div className="space-y-6" data-testid="enquiries-page">
      <PageHead eyebrow="CRM" title="Enquiries"
                subtitle="Capture loan enquiries, auto-route them to the matching branch, and drive the Admin → Telecaller → Sales workflow. Accepted enquiries convert into leads."
                actions={<Button data-testid="open-create-enquiry-btn" onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" /> Create Enquiry</Button>} />

      <Tabs tabs={tabs} active={tab} onChange={setTab} testid="enquiry-tabs" />

      {tab === "Enquiries" && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Total" value={stats.total} testid="enq-total" />
            <Stat label="Open" value={stats.open} testid="enq-open" />
            <Stat label="Converted" value={stats.converted} testid="enq-converted" />
            <Stat label="Not Interested" value={stats.closed} testid="enq-closed" />
          </div>
          <Panel title={`Enquiries (${rows.length})`} testid="enquiries-panel"
                 aside={<Select value={status} onChange={setStatus} testid="enquiry-status-filter"
                                options={[{ value: "all", label: "All statuses" },
                                          ...(m.statuses || []).map((s) => ({ value: s, label: s }))]} />}>
            <DataTable testid="enquiries-table" rows={rows} onRowClick={(r) => setDetail(r)}
                       empty="No enquiries yet. Share the QR code or create one manually."
                       columns={[
                         { key: "enquiry_id", label: "ID" },
                         { key: "customer_name", label: "Customer", render: (r) => (
                           <span className="flex items-center gap-1.5">
                             {r.customer_name}
                             {r.is_duplicate && (
                               <span data-testid={`dup-badge-${r.enquiry_id}`}
                                     className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                                 <AlertTriangle className="h-3 w-3" /> Dup</span>)}
                           </span>) },
                         { key: "mobile", label: "Mobile" },
                         { key: "product_type", label: "Product" },
                         { key: "loan_amount", label: "Amount", right: true, render: (r) => inr(r.loan_amount) },
                         { key: "city", label: "City" },
                         { key: "assigned_to_name", label: "Assigned To", render: (r) => r.assigned_to_name || r.assigned_to || "—" },
                         { key: "lead_source", label: "Source" },
                         { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                         { key: "created_at", label: "Created", render: (r) => fmtDate(r.created_at) },
                       ]} />
          </Panel>
        </>
      )}

      {tab === "QR Code" && (
        <Panel title="Public Enquiry QR Code" testid="qr-panel">
          <div className="flex flex-col items-start gap-6 sm:flex-row">
            <div ref={qrRef} className="rounded-xl border border-border bg-white p-4" data-testid="enquiry-qr">
              <QRCodeCanvas value={publicUrl} size={220} includeMargin level="M" />
            </div>
            <div className="space-y-3">
              <p className="max-w-md text-sm text-muted-foreground">
                Anyone can scan this code to open the public Create Enquiry form — no login needed.
                Submissions are auto-routed to the matching branch (or Admin).
              </p>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                <QrCode className="h-4 w-4 text-primary" />
                <span className="truncate" data-testid="public-url">{publicUrl}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={downloadQr} data-testid="download-qr-btn">
                  <Download className="mr-2 h-4 w-4" /> Download PNG</Button>
                <Button variant="outline" className="bg-white" onClick={printQr} data-testid="print-qr-btn">
                  <Printer className="mr-2 h-4 w-4" /> Print</Button>
                <Button variant="outline" className="bg-white" data-testid="copy-link-btn"
                        onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Link copied"); }}>
                  <Copy className="mr-2 h-4 w-4" /> Copy link</Button>
              </div>
            </div>
          </div>
        </Panel>
      )}

      {tab === "Branches" && isManager && <BranchesTab />}
      {tab === "Options" && isManager && <OptionsTab options={options} reload={meta.reload} />}

      <CreateEnquiryDialog open={createOpen} onClose={() => setCreateOpen(false)} form={form}
                           set={set} options={options} assignable={assignable} onSubmit={submitCreate} />
      <EnquiryDetailDialog detail={detail} onClose={() => setDetail(null)} assignable={assignable}
                           reloadList={list.reload} user={user} />
    </div>
  );
}

function CreateEnquiryDialog({ open, onClose, form, set, options, assignable, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto bg-white" data-testid="create-enquiry-dialog">
        <DialogHeader><DialogTitle>Create Enquiry</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Mobile" required>
            <Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
                   className="bg-white" data-testid="enq-mobile" /></Field>
          <Field label="First Name">
            <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)}
                   className="bg-white" data-testid="enq-first-name" /></Field>
          <Field label="Middle Name">
            <Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)}
                   className="bg-white" data-testid="enq-middle-name" /></Field>
          <Field label="Last Name">
            <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)}
                   className="bg-white" data-testid="enq-last-name" /></Field>
          <Field label="Product Type">
            <Select value={form.product_type} onChange={(v) => set("product_type", v)}
                    options={options.product_type || []} testid="enq-product-type" /></Field>
          <Field label="Required Loan Amount" required>
            <div className="flex items-center rounded-md border border-input bg-white pl-2">
              <span className="text-sm text-muted-foreground">₹</span>
              <Input type="number" value={form.loan_amount} onChange={(e) => set("loan_amount", e.target.value)}
                     className="border-0 bg-transparent" data-testid="enq-loan-amount" />
            </div></Field>
          <Field label="Alternate Phone">
            <Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)}
                   className="bg-white" data-testid="enq-alt-phone" /></Field>
          <Field label="Date of Birth">
            <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)}
                   className="bg-white" data-testid="enq-dob" /></Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                   className="bg-white" data-testid="enq-email" /></Field>
          <Field label="PAN No.">
            <Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())}
                   className="bg-white" data-testid="enq-pan" /></Field>
          <Field label="Employment Type">
            <Select value={form.employment_type} onChange={(v) => set("employment_type", v)}
                    options={options.employment_type || []} testid="enq-employment-type" /></Field>
          <Field label="Lead Source">
            <Select value={form.lead_source} onChange={(v) => set("lead_source", v)}
                    options={options.lead_source || []} testid="enq-lead-source" /></Field>
          <Field label="City">
            <Input value={form.city} onChange={(e) => set("city", e.target.value)}
                   className="bg-white" data-testid="enq-city" /></Field>
          <Field label="Pincode">
            <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)}
                   className="bg-white" data-testid="enq-pincode" /></Field>
          <Field label="Assign To">
            <Select value={form.assign_to} onChange={(v) => set("assign_to", v)}
                    options={assignable} testid="enq-assign-to" /></Field>
        </div>
        <Field label="Comments">
          <Textarea value={form.comment} onChange={(e) => set("comment", e.target.value)}
                    className="bg-white" rows={3} placeholder="Add any notes about this enquiry…"
                    data-testid="enq-comments" /></Field>
        <DialogFooter>
          <Button variant="outline" className="bg-white" onClick={onClose}>Cancel</Button>
          <Button onClick={onSubmit} data-testid="submit-enquiry-btn">Create Enquiry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnquiryDetailDialog({ detail, onClose, assignable, reloadList, user }) {
  const [assignTo, setAssignTo] = useState("");
  const [comment, setComment] = useState("");
  const [fresh, setFresh] = useState(null);
  const [dupes, setDupes] = useState([]);
  const e = fresh || detail;

  useEffect(() => {
    if (!detail?.enquiry_id) { setDupes([]); return; }
    api.get(`/crm/enquiries/${detail.enquiry_id}/duplicates`)
      .then(({ data }) => setDupes(data.duplicates || []))
      .catch(() => setDupes([]));
  }, [detail]);

  if (!detail) return null;
  const closed = ["Converted", "Not Interested", "Merged"].includes(e.status);
  const canAct = ["admin", "manager", "operations"].includes(user?.role)
    || (user?.email || "").toLowerCase() === (e.assigned_to || "").toLowerCase();

  const refresh = (data) => { setFresh(data); reloadList(); };
  const doAssign = async () => {
    if (!assignTo) return toast.error("Choose a team member");
    const r = await post(`/crm/enquiries/${e.enquiry_id}/assign`, { assign_to: assignTo, comment }, "Enquiry assigned");
    if (r) { setAssignTo(""); setComment(""); refresh(r); }
  };
  const doComment = async () => {
    if (!comment.trim()) return toast.error("Enter a comment");
    const r = await post(`/crm/enquiries/${e.enquiry_id}/comment`, { text: comment }, "Comment added");
    if (r) { setComment(""); refresh(r); }
  };
  const doNotInterested = async () => {
    const r = await post(`/crm/enquiries/${e.enquiry_id}/not-interested`, { comment }, "Marked Not Interested");
    if (r) { setComment(""); refresh(r); }
  };
  const closeAsDuplicate = async () => {
    const of = dupes.map((d) => d.enquiry_id).join(", ");
    const r = await post(`/crm/enquiries/${e.enquiry_id}/not-interested`,
      { comment: `Closed as duplicate of ${of}` }, "Closed as duplicate");
    if (r) refresh(r);
  };
  const doMerge = async (targetId) => {
    const r = await post(`/crm/enquiries/${e.enquiry_id}/merge`, { into: targetId },
      `Merged into ${targetId}`);
    if (r?.source_enquiry) { toast.success("Comments & history carried over"); refresh(r.source_enquiry); }
  };
  const doConvert = async () => {
    const r = await post(`/crm/enquiries/${e.enquiry_id}/convert`, {}, "Converted to lead");
    if (r?.enquiry) { toast.success(`Lead ${r.lead_id} created`); refresh(r.enquiry); }
  };
  const info = [
    ["Mobile", e.mobile], ["Alt Phone", e.alt_phone], ["Email", e.email], ["PAN", e.pan],
    ["Product", e.product_type], ["Amount", inr(e.loan_amount)], ["Employment", e.employment_type],
    ["DOB", e.dob], ["City", e.city], ["Pincode", e.pincode], ["Source", e.lead_source],
    ["Assigned To", e.assigned_to_name || e.assigned_to || "—"],
  ];

  return (
    <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setFresh(null); onClose(); } }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-testid="enquiry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {e.customer_name} <StatusBadge value={e.status} />
            <span className="text-xs font-normal text-muted-foreground">{e.enquiry_id}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-border p-4 text-sm sm:grid-cols-3">
          {info.map(([k, v]) => (
            <div key={k}><p className="text-[10px] uppercase text-muted-foreground">{k}</p>
              <p className="truncate font-medium">{v || "—"}</p></div>
          ))}
        </div>
        {e.converted_lead_id && (
          <p className="text-sm text-green-700" data-testid="converted-lead-note">
            Converted to lead <b>{e.converted_lead_id}</b>.</p>
        )}
        {e.merged_into && (
          <p className="text-sm text-amber-700" data-testid="merged-into-note">
            This enquiry was merged into <b>{e.merged_into}</b>.</p>
        )}

        {dupes.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="duplicates-panel">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              {dupes.length} possible duplicate{dupes.length > 1 ? "s" : ""} — same mobile or PAN
            </div>
            <div className="mt-2 space-y-1.5">
              {dupes.map((d) => (
                <div key={d.enquiry_id} className="flex flex-wrap items-center gap-2 rounded-md bg-white/70 px-2 py-1.5 text-xs"
                     data-testid={`duplicate-row-${d.enquiry_id}`}>
                  <b>{d.enquiry_id}</b><span>{d.customer_name}</span>
                  <span className="text-muted-foreground">{d.mobile}</span>
                  <StatusBadge value={d.status} />
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">matches {d.match}</span>
                  <span className="text-muted-foreground">{d.assigned_to_name || d.assigned_to || "—"}</span>
                  {canAct && !closed && d.status !== "Merged" && (
                    <Button size="sm" variant="outline"
                            className="ml-auto h-6 border-amber-400 bg-white px-2 text-[10px] text-amber-800 hover:bg-amber-100"
                            onClick={() => doMerge(d.enquiry_id)} data-testid={`merge-into-${d.enquiry_id}`}>
                      Merge into this</Button>)}
                </div>
              ))}
            </div>
            {canAct && !closed && (
              <p className="mt-2 text-[11px] text-amber-700">
                "Merge into this" moves this enquiry's comments &amp; history into the chosen original and closes this one as Merged.
              </p>
            )}
            {canAct && !closed && (
              <Button size="sm" variant="outline" className="mt-3 border-amber-300 bg-white text-amber-700 hover:bg-amber-100"
                      onClick={closeAsDuplicate} data-testid="close-as-duplicate-btn">
                Mark as duplicate & close (no merge)</Button>
            )}
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Comments</p>
          <div className="max-h-40 space-y-2 overflow-y-auto" data-testid="enquiry-comments-list">
            {(e.comments || []).length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
            {(e.comments || []).map((c, i) => (
              <div key={i} className="rounded-md border border-border/60 bg-muted/30 p-2 text-sm">
                <p>{c.text}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">{c.by_name || c.by} · {fmtDate(c.at)}</p>
              </div>
            ))}
          </div>
        </div>

        {canAct && !closed && (
          <div className="space-y-3 rounded-lg border border-border p-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs">Assign to team member</Label>
                <Select value={assignTo} onChange={setAssignTo} options={assignable}
                        testid="detail-assign-select" />
              </div>
              <div className="flex items-end">
                <Button className="w-full" onClick={doAssign} data-testid="detail-assign-btn">Assign</Button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Comment / note</Label>
              <Textarea value={comment} onChange={(e2) => setComment(e2.target.value)} rows={2}
                        className="bg-white" data-testid="detail-comment-input" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="bg-white" onClick={doComment} data-testid="detail-add-comment-btn">
                Add comment</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={doConvert} data-testid="detail-convert-btn">
                Convert to Lead</Button>
              <Button variant="outline" className="border-red-200 bg-white text-red-600 hover:bg-red-50"
                      onClick={doNotInterested} data-testid="detail-not-interested-btn">
                Not Interested</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BranchesTab() {
  const branches = useApi("/crm/branches");
  const users = useApi("/crm/users");
  const [form, setForm] = useState({ name: "", city: "", pincode: "", branch_admin: "", status: "Active" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const adminOpts = (users.data || []).map((u) => ({ value: u.email, label: `${u.name} (${u.role})` }));
  const submit = async () => {
    const r = await post("/crm/branches", form, "Branch created");
    if (r) { setForm({ name: "", city: "", pincode: "", branch_admin: "", status: "Active" }); branches.reload(); }
  };
  return (
    <div className="space-y-6">
      <Panel title="Add branch" testid="add-branch-panel">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Branch name" required>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white" data-testid="branch-name" /></Field>
          <Field label="City" required>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="bg-white" data-testid="branch-city" /></Field>
          <Field label="Pincode" required>
            <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className="bg-white" data-testid="branch-pincode" /></Field>
          <Field label="Branch admin (user)">
            <Select value={form.branch_admin} onChange={(v) => set("branch_admin", v)} options={adminOpts} testid="branch-admin" /></Field>
          <div className="flex items-end">
            <Button className="w-full" onClick={submit} data-testid="submit-branch-btn">Add branch</Button>
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Incoming enquiries are auto-assigned to the branch matching the customer's city AND pincode.
          If none matches, they go to the global Admin.
        </p>
      </Panel>
      <Panel title={`Branches (${(branches.data || []).length})`} testid="branches-panel">
        <DataTable testid="branches-table" rows={branches.data || []} empty="No branches yet."
                   columns={[{ key: "branch_id", label: "ID" }, { key: "name", label: "Name" },
                             { key: "city", label: "City" }, { key: "pincode", label: "Pincode" },
                             { key: "branch_admin", label: "Branch admin" },
                             { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
      </Panel>
    </div>
  );
}

function OptionsTab({ options, reload }) {
  const [draft, setDraft] = useState({
    product_type: (options.product_type || []).join("\n"),
    employment_type: (options.employment_type || []).join("\n"),
    lead_source: (options.lead_source || []).join("\n"),
  });
  const save = async () => {
    const body = Object.fromEntries(Object.entries(draft).map(
      ([k, v]) => [k, v.split("\n").map((s) => s.trim()).filter(Boolean)]));
    const r = await put("/crm/enquiry-options", body, "Options updated");
    if (r) reload();
  };
  return (
    <Panel title="Dropdown options" testid="options-panel"
           aside={<Button onClick={save} data-testid="save-options-btn">Save options</Button>}>
      <div className="grid gap-4 sm:grid-cols-3">
        {[["product_type", "Product Type"], ["employment_type", "Employment Type"], ["lead_source", "Lead Source"]].map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{label} (one per line)</Label>
            <Textarea rows={7} value={draft[k]} className="bg-white" data-testid={`options-${k}`}
                      onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))} />
          </div>
        ))}
      </div>
    </Panel>
  );
}
